import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaSearch, 
  FaUsers, 
  FaPlus, 
  FaLocationArrow, 
  FaLock, 
  FaGlobe, 
  FaUserPlus
} from 'react-icons/fa';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import { useToast } from '../hooks/ToastContext';
import Spinner from '../common/Spinner';
import { 
  CustomButton2,
  CustomButton6,
  CustomInputBox2,
  ModernCard
} from '../Templates';
import './styles/GroupsPage.css';

const GroupsPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [myGroups, setMyGroups] = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [activeTab, setActiveTab] = useState('discover');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([
    'all',
    'commuters',
    'sports',
    'nature',
    'tourism',
    'hobby',
    'family',
    'other'
  ]);

  // Fetch groups data
  useEffect(() => {
    const fetchGroups = async () => {
      if (!user) return;

      try {
        setLoading(true);
        
        // Fetch user's groups
        const { data: userGroups, error: userGroupsError } = await supabase.rpc(
          'get_user_groups',
          { p_user_id: user.id }
        );
        
        if (userGroupsError) throw userGroupsError;
        setMyGroups(userGroups || []);
        
        // Fetch public groups
        const { data: groups, error: groupsError } = await supabase.rpc(
          'search_groups',
          { 
            p_search_term: searchTerm || null,
            p_category: selectedCategory !== 'all' ? selectedCategory : null,
            p_location: null
          }
        );
        
        if (groupsError) throw groupsError;
        
        // Filter out groups the user is already a member of
        if (groups && userGroups) {
          // Extract IDs of groups the user is a member of
          const userGroupIds = userGroups.map(g => g.group_id);
          
          // Only filter out groups the user is a member of
          const filteredGroups = groups.filter(group => !userGroupIds.includes(group.id));
          setPublicGroups(filteredGroups || []);
        } else {
          setPublicGroups(groups || []);
        }
      } catch (error) {
        console.error('Error fetching groups:', error);
        addToast({
          type: 'error',
          title: t('errors.fetch_error'),
          description: t('errors.fetch_error_description')
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchGroups();
  }, [user, searchTerm, selectedCategory, t, addToast]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    // The search is already reactive with searchTerm state
  };

  // Handle key down for search
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch(e);
    }
  };

  // Create new group
  const handleCreateGroup = () => {
    navigate('/groups/create');
  };

  // View group details
  const handleViewGroup = (groupId) => {
    navigate(`/groups/${groupId}`);
  };

  // Join a public group
  const handleJoinGroup = async (e, groupId) => {
    e.stopPropagation();
    
    if (!user) {
      addToast({
        type: 'error',
        title: t('errors.authentication_required'),
        description: t('errors.login_required_action')
      });
      return;
    }
    
    try {
      // Check if user can join the group
      const { data: canJoin, error: joinCheckError } = await supabase.rpc(
        'can_join_group',
        { p_group_id: groupId, p_user_id: user.id }
      );
      
      if (joinCheckError) throw joinCheckError;
      
      if (!canJoin) {
        addToast({
          type: 'error',
          title: t('errors.join_error'),
          description: t('errors.cannot_join_group')
        });
        return;
      }
      
      // Check if user was previously a member
      const { data: existingMembership, error: membershipError } = await supabase
        .from('group_members')
        .select('*')
        .eq('group_id', groupId)
        .eq('user_id', user.id);
        
      if (membershipError) throw membershipError;
      
      if (existingMembership && existingMembership.length > 0) {
        // Update existing membership to active
        const { error: updateError } = await supabase
          .from('group_members')
          .update({ status: 'active' })
          .eq('id', existingMembership[0].id);
          
        if (updateError) throw updateError;
      } else {
        // Join the group (create new membership)
        const { error: joinError } = await supabase
          .from('group_members')
          .insert({
            group_id: groupId,
            user_id: user.id,
            role: 'member',
            status: 'active'
          });
        
        if (joinError) throw joinError;
      }
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.join_success')
      });
      
      // Refresh the groups lists
      const filteredPublicGroups = publicGroups.filter(group => group.id !== groupId);
      setPublicGroups(filteredPublicGroups);
      
      // Fetch the joined group details and add to myGroups
      const { data: joinedGroup, error: groupError } = await supabase
        .from('groups')
        .select('id, name')
        .eq('id', groupId)
        .single();
      
      if (!groupError && joinedGroup) {
        setMyGroups([...myGroups, {
          group_id: joinedGroup.id,
          group_name: joinedGroup.name,
          role: 'member',
          member_count: 1,
          is_creator: false
        }]);
      }
      
    } catch (error) {
      console.error('Error joining group:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.join_group_failed')
      });
    }
  };

  // Request to join a private group
  const handleRequestJoin = async (e, groupId) => {
    e.stopPropagation();
    
    if (!user) {
      addToast({
        type: 'error',
        title: t('errors.authentication_required'),
        description: t('errors.login_required_action')
      });
      return;
    }
    
    try {
      // Check if a request already exists
      const { data: existingRequests, error: checkError } = await supabase
        .from('group_join_requests')
        .select('id, status')
        .eq('group_id', groupId)
        .eq('user_id', user.id);
      
      if (checkError) throw checkError;
      
      // If there's a pending request, don't create a new one
      if (existingRequests && existingRequests.length > 0) {
        const activeRequest = existingRequests.find(req => req.status === 'pending');
        if (activeRequest) {
          addToast({
            type: 'info',
            title: t('info'),
            description: t('groups.request_already_pending')
          });
          return;
        }
      }
      
      // Create the join request
      const { error: requestError } = await supabase
        .from('group_join_requests')
        .insert({
          group_id: groupId,
          user_id: user.id,
          status: 'pending'
        });
      
      if (requestError) throw requestError;
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.request_sent')
      });
      
    } catch (error) {
      console.error('Error requesting to join group:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.request_join_failed')
      });
    }
  };

  if (loading) {
    return (
      <div className="groups-page">
        <div className="spinner-container">
          <Spinner size="large" customText={t('app.loading')} />
        </div>
      </div>
    );
  }

  return (
    <div className="groups-page">
      {/* Header with title and create button */}
      <div className="groups-header">
        <h1 className="groups-title">{t('groups.title')}</h1>
        <CustomButton6
          label={t('groups.create_group')}
          onClick={handleCreateGroup}
        />
      </div>
      
      {/* Search bar */}
      <form className="groups-search-bar" onSubmit={handleSearch}>
        <CustomInputBox2
          type="text"
          placeholder={t('groups.search_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <CustomButton2
          label={t('buttons.search')}
          type="submit"
          icon={<FaSearch />}
        />
      </form>
      
      {/* Category filters */}
      <div className="groups-filters">
        {categories.map(category => (
          <div
            key={category}
            className={`groups-filter ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {t(`groups.categories.${category}`)}
          </div>
        ))}
      </div>
      
      {/* Tabs for discover and my groups */}
      <div className="groups-tabs">
        <div
          className={`groups-tab ${activeTab === 'discover' ? 'active' : ''}`}
          onClick={() => setActiveTab('discover')}
        >
          {t('groups.discover')}
        </div>
        <div
          className={`groups-tab ${activeTab === 'my-groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('my-groups')}
        >
          {t('groups.my_groups')} ({myGroups.length})
        </div>
      </div>
      
      {/* Content area */}
      <div className="groups-content">
        {activeTab === 'discover' && (
          <>
            {publicGroups.length === 0 ? (
              <div className="groups-empty">
                <h3>{t('groups.no_groups_found')}</h3>
                <p>{t('groups.try_different_filters')}</p>
                <CustomButton6
                  label={t('groups.create_first_group')}
                  onClick={handleCreateGroup}
                />
              </div>
            ) : (
              <div className="groups-list">
                {publicGroups.map(group => (
                  <ModernCard
                    key={group.id}
                    className="group-card"
                    onClick={() => handleViewGroup(group.id)}
                  >
                    <div
                      className={`group-card-banner ${!group.banner_image_url ? 'no-image' : ''}`}
                      style={{
                        backgroundImage: group.banner_image_url
                          ? `url(${group.banner_image_url}?t=${Date.now()})`
                          : 'none'
                      }}
                    >
                      {group.category && (
                        <div className="group-card-category">
                          {t(`groups.categories.${group.category}`)}
                        </div>
                      )}
                      <div className="group-card-visibility">
                        {group.visibility === 'public' ? (
                          <><FaGlobe size={12} /> {t('groups.public')}</>
                        ) : (
                          <><FaLock size={12} /> {t('groups.private')}</>
                        )}
                      </div>
                    </div>
                    <div className="group-card-content"
                          onClick={() => handleViewGroup(group.id)}
                          >
                      <h3 className="group-card-title">{group.name}</h3>
                      <p className="group-card-description">
                        {group.description || t('groups.no_description')}
                      </p>
                      <div className="group-card-footer">
                        <div className="group-card-members">
                          <FaUsers />
                          {group.member_count} {t('groups.members')}
                        </div>
                        {group.location && (
                          <div className="group-card-location">
                            <FaLocationArrow />
                            {group.location}
                          </div>
                        )}
                      </div>
                      <div className="group-actions" onClick={e => e.stopPropagation()}>
                        {group.visibility === 'public' ? (
                          <CustomButton2
                            label={t('groups.join')}
                            onClick={(e) => handleJoinGroup(e, group.id)}
                            icon={<FaUsers />}
                          />
                        ) : (
                          <CustomButton2
                            label={t('groups.request_join')}
                            onClick={(e) => handleRequestJoin(e, group.id)}
                            icon={<FaUserPlus />}
                          />
                        )}
                      </div>
                    </div>
                  </ModernCard>
                ))}
              </div>
            )}
          </>
        )}
        
        {activeTab === 'my-groups' && (
          <>
            {myGroups.length === 0 ? (
              <div className="groups-empty">
                <h3>{t('groups.no_joined_groups')}</h3>
                <p>{t('groups.join_or_create')}</p>
                <CustomButton2
                  label={t('groups.discover_groups')}
                  onClick={() => setActiveTab('discover')}
                />
              </div>
            ) : (
              <div className="groups-list">
                {myGroups.map(group => (
                  <ModernCard
                    key={group.group_id}
                    className="group-card"
                    onClick={() => handleViewGroup(group.group_id)}
                  >
                    <div 
                      className={`group-card-banner ${!group.banner_image_url ? 'no-image' : ''}`}
                      style={{
                        backgroundImage: group.banner_image_url
                          ? `url(${group.banner_image_url}?t=${Date.now()})`
                          : 'none'
                      }}
                      onClick={() => handleViewGroup(group.group_id)}
                    >
                      <div className="group-card-category">
                        {group.is_creator 
                          ? t('groups.you_created')
                          : t(`groups.roles.${group.role}`)}
                      </div>
                    </div>
                    <div className="group-card-content"
                         onClick={() => handleViewGroup(group.group_id)}
                    >
                      <h3 className="group-card-title">{group.group_name}</h3>
                      <div className="group-card-footer">
                        <div className="group-card-members">
                          <FaUsers />
                          {group.member_count} {t('groups.members')}
                        </div>
                      </div>
                    </div>
                  </ModernCard>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GroupsPage;